import { useSelector, useDispatch } from 'react-redux'
import { useEffect } from 'react'
import {
  getAssetReducer,
  setAssetReducer,
  createAssetReducer,
  editAssetReducer,
  deleteAssetReducer,
  getAssetUsersReducer,
  addAssetUserReducer,
  deleteAssetUserReducer,
  bulkDeleteAssetReducer,
  setAssetDetailPageStatus,
  setAssetUsersReducer,
  downloadAssetReducer,
  downloadAssetsReducer,
  setUserAssetPermissionsReducer,
  downloadLinkedAssetReducer,
  setAssetActivityReducer,
  getAssetActivityReducer,
  process3DFileApiReducer,
  getAssetReportsReducer,
  setAssetReportsReducer,
  setAssetReportsIsLoading,
  createAssetReportReducer,
  resolveAssetReportReducer,
} from './slice'
import {
  assetDetailPageAssetSelector,
  assetDetailPageStatusSelector,
  assetUsersSelector,
  linkedAssetSelector,
  newFilesStatusValuesSelector,
  userAssetPermissionsSelector,
  assetActivitySelector,
  assetActivityIsLoadingSelector,
  assetReportsSelector,
  assetReportsIsLoadingSelector,
} from './selectors'
import { IAsset, ICreateAssetFileValues, IEditAssetModalFile, IUserAssetPermissions } from './types'
import { useNavigate, useLocation } from 'react-router-dom'
import { get, some } from 'lodash'
import { PageStatus } from 'store/client'
import { PermissionView } from 'gallerydigitalassets-v1-client'
import { useAppEnv } from 'store/env/hook'
import { useEnv } from '@praxis/component-runtime-env'
import { ITag } from '../tags'
import { useTaxonomy } from '../taxonomy'
import { AssetReportCreate, AssetReportResolve } from '@galleryjs/api-digital-assets'
import { useSearch } from 'store/search'
import { findFunkyFiltersByKey } from 'store/search/utils'

export const useAsset = (collectionId?: string, taxonomyId?: string, isHomepage?: boolean) => {
  const assetDetailPageAsset = useSelector(assetDetailPageAssetSelector)
  const assetUsers = useSelector(assetUsersSelector)
  const newFilesStatusValues = useSelector(newFilesStatusValuesSelector)
  const assetDetailPageStatus: PageStatus = useSelector(assetDetailPageStatusSelector)
  const userAssetPermissions: IUserAssetPermissions = useSelector(userAssetPermissionsSelector)
  const linkedAssets: IAsset[] | undefined = useSelector(linkedAssetSelector)
  const assetActivity = useSelector(assetActivitySelector)
  const assetActivityIsLoading = useSelector(assetActivityIsLoadingSelector)
  const assetReports = useSelector(assetReportsSelector)
  const assetReportsIsLoading = useSelector(assetReportsIsLoadingSelector)
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { environment } = useAppEnv()
  const {
    assetStatus,
    assetActivityServer,
    defaultCollectionPageSize,
    customSearchFilters,
    funkyFilterKeys,
    searchServer,
    searchSecretKey,
  } = useEnv()
  const { taxonomies, getTaxonomyNodeById } = useTaxonomy()
  const { querySearchCriteria } = useSearch()
  const SEARCH_API_KEY = searchSecretKey || process.env.REACT_APP_SEARCH_SECRET_KEY
  const searchFunkyFilters = findFunkyFiltersByKey(funkyFilterKeys, customSearchFilters)

  // captures path of previous page, if deep-linked to page, pathname === ''
  const pathname: string = get(location, 'state.location.pathname', '')

  const getAsset = async (id: string) => {
    dispatch(getAssetReducer(id))
  }

  const getAssetStatus = (assetTags: ITag[] | undefined, taxonomyId: string) => {
    const assetTaxonomy = getTaxonomyNodeById(Array.from(taxonomies), taxonomyId)
    if (assetTaxonomy && assetStatus.taxonomies.includes(assetTaxonomy.name.toLowerCase())) {
      const tcinTagPresent = assetTags?.filter((assetTag: ITag) => assetTag.tag.key.toLowerCase() === 'tcin')
      if (tcinTagPresent && tcinTagPresent.length > 0) {
        const assetStatusTags = assetTags?.filter((assetTag: ITag) => assetTag.tag.key === assetStatus.key)
        const activeStatusTags = assetStatusTags?.filter(
          (assetStatusTag: ITag) => assetStatusTag.tag.value === assetStatus.activeValue
        ).length
        return activeStatusTags ? (activeStatusTags > 0 ? 'Active' : 'Inactive') : 'Inactive'
      }
    }
    return ''
  }

  const setAsset = async (asset: IAsset | undefined) => {
    dispatch(setAssetReducer(asset))
  }

  const setAssetDetailStatus = (status: PageStatus) => {
    dispatch(setAssetDetailPageStatus(status))
  }

  useEffect(() => {
    const asset = assetDetailPageAsset
    // allows editing and deleting of asset if user is organization admin
    const isOrgAdmin = Array.from(asset?.permissions || []).filter(
      (permission: PermissionView) =>
        permission.access === PermissionView.AccessEnum.OrganizationAdmin ||
        permission.access === PermissionView.AccessEnum.Admin
    )

    // allows editing and deleting of asset if user is asset creator
    const isAssetCreator = Array.from(asset?.permissions || []).filter(
      (permission: PermissionView) => permission.access === PermissionView.AccessEnum.AssetCreator
    )

    // allows editing and deleting of asset if user is asset manager

    const assetManager = Array.from(asset?.permissions || []).filter(
      (permission: PermissionView) => permission.access === PermissionView.AccessEnum.AssetManager
    )

    const payload: IUserAssetPermissions = {
      userCanEditAndDeleteAsset: some(isOrgAdmin) || some(isAssetCreator),
      userIsAssetManager: some(assetManager),
    }

    dispatch(setUserAssetPermissionsReducer(payload))
  }, [assetDetailPageAsset])

  const createAsset = async (
    name: string,
    files: ICreateAssetFileValues[],
    isAssetPublic: boolean,
    taxonomyId: string,
    onSuccessCallback: (assetId: string) => void,
    onErrorCallback: () => void
  ) => {
    const payload = {
      name,
      files,
      isAssetPublic,
      taxonomyId,
      onSuccessCallback,
      onErrorCallback,
    }
    dispatch(createAssetReducer(payload))
  }

  const process3DFile = async (assetId: string[], pipelineId?: string) => {
    const payload = {
      assetId,
      pipelineId,
    }
    dispatch(process3DFileApiReducer(payload))
  }

  const editAsset = async (
    assetId: string,
    editRequest: {
      initialAssetName: string
      newAssetName: string
      initialIsPublic: boolean
      newIsPublic: boolean
      initialTaxonomyId: string
      newTaxonomyId: string
    },
    filesToAdd: IEditAssetModalFile[],
    filesToRemove: IEditAssetModalFile[],
    onSuccess: () => void
  ) => {
    const payload = {
      assetId,
      editRequest,
      filesToAdd,
      filesToRemove,
      onSuccess,
    }
    dispatch(editAssetReducer(payload))
  }

  const deleteAsset = async (id: string) => {
    const payload = {
      id,
      pathname,
      navigate,
    }
    dispatch(deleteAssetReducer(payload))
  }

  const getAssetUsers = async (assetId: string, permission: string = 'EDIT') => {
    const payload = {
      assetId,
      permission,
    }
    dispatch(getAssetUsersReducer(payload))
  }

  const deleteAssetUser = async (assetId: string, userId: string) => {
    const payload = {
      assetId,
      userId,
    }
    dispatch(deleteAssetUserReducer(payload))
  }

  const addAssetUser = async (
    assetId: string,
    userId: string[],
    access:
      | 'NONE'
      | 'ADMIN'
      | 'ORGANIZATION_ADMIN'
      | 'WORKFLOW_ADMIN'
      | 'ORGANIZATION_ASSET_CREATOR'
      | 'ORGANIZATION_ASSET_MANAGER'
      | 'ORGANIZATION_ASSET_VIEWER'
      | 'ORGANIZATION_GALLERY_VIEWER'
      | 'ORGANIZATION_GALLERY_MANAGER'
      | 'ORGANIZATION_GALLERY_CREATOR'
      | 'GALLERY_CREATOR'
      | 'GALLERY_MANAGER'
      | 'GALLERY_VIEWER'
      | 'ASSET_CREATOR'
      | 'ASSET_MANAGER'
      | 'ASSET_VIEWER'
      | 'WORKFLOW_REVIEWER'
      | 'WORKFLOW_RENDER_CREATOR'
      | 'WORKFLOW_DEFAULT'
  ) => {
    const payload = {
      assetId,
      userId,
      access,
    }
    dispatch(addAssetUserReducer(payload))
  }

  const resetUsers = async () => {
    dispatch(setAssetUsersReducer([]))
  }

  const deleteAssets = async (assetIds: string[], setIsBulkDelete: React.Dispatch<React.SetStateAction<boolean>>) => {
    setIsBulkDelete(true)
    const softDelete = environment === 'prod' ? true : false
    const payload = {
      assetIds,
      softDelete,
      galleryId: collectionId,
      isHomepage,
      size: defaultCollectionPageSize,
      taxonomyId,
      querySearchCriteria,
      SEARCH_API_KEY,
      searchFunkyFilters,
      searchServer,
    }
    await dispatch(bulkDeleteAssetReducer(payload))
  }

  const downloadAsset = async (assetId: string) => {
    const payload = { assetId }
    dispatch(downloadAssetReducer(payload))
  }

  const downloadLinkedAssets = async (assetId: string) => {
    const payload = { assetId }
    dispatch(downloadLinkedAssetReducer(payload))
  }

  const downloadAssetsAsync = async (e: React.MouseEvent<HTMLSpanElement>, assetIds: string[]) => {
    e.preventDefault()
    const payload = { assetIds }
    dispatch(downloadAssetsReducer(payload))
  }

  const downloadAssetLocally = (e: React.MouseEvent<HTMLSpanElement>, assetId: string) => {
    e.preventDefault()
    downloadAsset(assetId)
  }

  const downloadAssetsLocally = (e: React.MouseEvent<HTMLSpanElement>, assetIds: string[]) => {
    e.preventDefault()
    const payload = { assetIds }
    dispatch(downloadAssetsReducer(payload))
  }

  const sendAssetToGD = (assetId: string) => {
    window.location.href = `gallery://${environment}/asset/${assetId}`
  }

  const getAssetActivity = async (assetId: string) => {
    dispatch(getAssetActivityReducer({ assetActivityServer: assetActivityServer, assetId: assetId }))
  }

  const setAssetActivity = async (assetId: string) => {
    dispatch(setAssetActivityReducer({ assetActivityServer: assetActivityServer, assetId: assetId }))
  }

  const getAssetReports = async (assetId: string) => {
    dispatch(getAssetReportsReducer({ assetId }))
  }

  const createAssetReport = async (
    createAssetReportRequest: AssetReportCreate,
    onSuccess: Function,
    onError: Function
  ) => {
    dispatch(createAssetReportReducer({ createAssetReportRequest, onSuccess, onError }))
  }

  const resolveAssetReport = async (
    resolveAssetReportPayload: AssetReportResolve,
    onSuccess: Function,
    onError: Function
  ) => {
    dispatch(resolveAssetReportReducer({ resolveAssetReportPayload, onSuccess, onError }))
  }

  const resetAssetReports = async () => {
    dispatch(setAssetReportsReducer([]))
    dispatch(setAssetReportsIsLoading(false))
  }

  return {
    getAsset,
    setAsset,
    setAssetDetailStatus,
    createAsset,
    editAsset,
    deleteAsset,
    getAssetUsers,
    deleteAssets,
    deleteAssetUser,
    addAssetUser,
    assetDetailPageAsset,
    assetDetailPageStatus,
    assetUsers,
    newFilesStatusValues,
    userAssetPermissions,
    linkedAssets,
    resetUsers,
    downloadAsset,
    downloadAssetLocally,
    downloadAssetsLocally,
    downloadLinkedAssets,
    sendAssetToGD,
    downloadAssetsAsync,
    getAssetStatus,
    setAssetActivity,
    getAssetActivity,
    assetActivity,
    assetActivityIsLoading,
    process3DFile,
    assetReports,
    assetReportsIsLoading,
    getAssetReports,
    createAssetReport,
    resolveAssetReport,
    resetAssetReports,
  }
}
